// CalendarSummaryView — the "Calendar" view: a GitHub-style month heatmap of
// the days this config's sources recorded a check-in, the consistency/streaks
// companion to the streaks stats (active days / longest / current).
//
// Stateless: it reuses the card's already-fetched `activeDates` (the counted
// days, from the 'active-dates' source) and `pagingDates` (every day with data)
// so it needs no fetch of its own. Reuses the shared ActivityCalendar
// component, so the grid geometry reads identically to the feature/project
// activity tabs — but the cell *coloring* here is streak-aware rather than the
// plain green intensity ramp.
//
// active-dates is presence-only (no per-day count), so a day is "logged" or
// not. On top of that binary, we light up the two runs that matter for
// consistency: the CURRENT run (the streak ending at your latest active day)
// and the LONGEST consecutive run (your record). When the current run *is* the
// record they're the same days and read as one (longest) color. The same two
// numbers are surfaced as a compact compare-at-a-glance banner above the grid.
//
// Colors come from the shared chart palette (SLICE_COLORS, the same set the
// donut/bar bodies draw from) so the calendar reads as part of the same chart
// family rather than its own ad-hoc green/amber scheme.
//
// Streaks are computed here on the frontend, windowed to the selected period so
// the highlighted cells, the banner numbers, and the sibling Stats tab's
// backend-computed longest/current all agree. The run definition mirrors the
// backend's `_streak_lengths`: maximal consecutive calendar days; `current` is
// the run ending at the most recent active day in scope, and only while that
// run is still alive — one day of slack for today, then it's over and reads 0.

import React, { useEffect, useMemo } from 'react'
import { motion } from 'motion/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import ActivityCalendar, { HexCell } from '../../ActivityCalendar'
import { useHorizontalOverflow } from '../../../hooks/useHorizontalOverflow'
import type { DateWindow } from '../../../charts/windowRange'
import type { SummaryViewBodyProps } from '../viewRegistry'
import StreakStripView from './StreakStripView'
import {
  CURRENT_COLOR,
  LONGEST_COLOR,
  LOGGED_COLOR,
  EMPTY_COLOR,
  COVERAGE_COLOR,
  StreakBanner,
  CoverageBanner,
} from './streakCommon'

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const dayKey = (ms: number): string => {
  const d = new Date(ms)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** A local calendar day reduced to a comparable integer ordinal plus its key.
 *  Rounding the local-midnight ms to whole days makes consecutive-day math
 *  (ordinal diff === 1) robust to DST shifting the UTC offset by an hour. */
function dayParts(ms: number): { ord: number; key: string } {
  const d = new Date(ms)
  const y = d.getFullYear()
  const m = d.getMonth()
  const day = d.getDate()
  const ord = Math.round(new Date(y, m, day).getTime() / 86_400_000)
  const key = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  return { ord, key }
}

export interface StreakInfo {
  /** Length of the run ending at the most recent active day in scope — 0 when
   *  that run is no longer alive (see `computeStreaks`). */
  current: number
  /** Length of the longest consecutive run in scope. */
  longest: number
  /** YYYY-MM-DD keys of the days in the current run. */
  currentDays: Set<string>
  /** YYYY-MM-DD keys of the days in the longest run. */
  longestDays: Set<string>
  /** True when the current run *is* the record run (same days, > 0). */
  isRecord: boolean
}

/** Longest + current consecutive-day runs within `activeDates`, restricted to a
 *  bounded window (unbounded → all dates). Ties for "longest" resolve to the
 *  most recent run, so a current run that matches the record reads as the
 *  record (isRecord), which is the motivating interpretation.
 *
 *  The latest run counts as CURRENT only while it's still alive: its last day
 *  has to be the reference day or the one before it. The reference day is the
 *  window's last day clamped to today, so a window running to the present
 *  measures against today and a past window measures against its own end
 *  ("was the streak alive when this month closed?"). The single day of slack is
 *  today itself — it isn't over, so logging yesterday still counts. A run that
 *  ended earlier is broken: `current` is 0 and `currentDays` empty, so the grid
 *  drops the green highlight (those days stay gold if they're the record).
 *  Mirrors the backend's `_streak_lengths` and the strip's `computeStrip`.
 *  `todayMs` is injected so the rule is testable. */
export function computeStreaks(
  activeDates: number[],
  window: DateWindow,
  todayMs: number = Date.now(),
): StreakInfo {
  const { start, end } = window
  const inScope =
    Number.isFinite(start) && Number.isFinite(end)
      ? activeDates.filter((ms) => ms >= start && ms < end)
      : activeDates

  // Unique calendar days → ordinal, keeping the key for highlight lookup.
  const byOrd = new Map<number, string>()
  for (const ms of inScope) {
    const { ord, key } = dayParts(ms)
    byOrd.set(ord, key)
  }
  const empty: StreakInfo = {
    current: 0, longest: 0, currentDays: new Set(), longestDays: new Set(), isRecord: false,
  }
  if (byOrd.size === 0) return empty

  const ords = [...byOrd.keys()].sort((a, b) => a - b)
  const runs: number[][] = []
  let run: number[] = [ords[0]]
  for (let i = 1; i < ords.length; i++) {
    if (ords[i] === ords[i - 1] + 1) run.push(ords[i])
    else { runs.push(run); run = [ords[i]] }
  }
  runs.push(run)

  const latestRun = runs[runs.length - 1]
  // `>=` so a tie resolves to the latest run; since latestRun is iterated
  // last, a live run tying the record wins identity → isRecord.
  let longestRun = runs[0]
  for (const r of runs) if (r.length >= longestRun.length) longestRun = r

  // The reference day: the window's last day, never past today.
  const todayOrd = dayParts(todayMs).ord
  const refOrd = Number.isFinite(end)
    ? Math.min(dayParts(end - 1).ord, todayOrd)
    : todayOrd
  const alive = latestRun[latestRun.length - 1] >= refOrd - 1
  const currentRun = alive ? latestRun : []

  const toKeys = (r: number[]) => new Set(r.map((o) => byOrd.get(o) as string))
  return {
    current: currentRun.length,
    longest: longestRun.length,
    currentDays: toKeys(currentRun),
    longestDays: toKeys(longestRun),
    isRecord: alive && latestRun === longestRun,
  }
}

/** Calendar months (year + 0-based month) spanning [startMs, endMs). */
function monthsInRange(startMs: number, endMs: number): Array<{ year: number; month: number }> {
  const out: Array<{ year: number; month: number }> = []
  const s = new Date(startMs)
  const last = new Date(endMs - 1) // end is exclusive
  let y = s.getFullYear()
  let m = s.getMonth()
  // Guard against an inverted/empty range.
  if (endMs <= startMs) return out
  while (y < last.getFullYear() || (y === last.getFullYear() && m <= last.getMonth())) {
    out.push({ year: y, month: m })
    m += 1
    if (m > 11) { m = 0; y += 1 }
  }
  return out
}

// Dispatcher: the 'calendar' view id covers both streak visuals. Cadence streaks
// (weekly / monthly events) can't read on a daily heatmap — a once-a-week event
// leaves it nearly empty and pegs every run at 1 — so they hand off to the 1-D
// period strip; daily keeps the month grid. Kept hook-free so the conditional
// return is legal (each branch's hooks live in its own component).
const CalendarSummaryView: React.FC<SummaryViewBodyProps> = (props) => {
  const cadence = (props.config?.params as { cadence?: string } | null | undefined)?.cadence
  if (cadence === 'weekly' || cadence === 'monthly') {
    return <StreakStripView {...props} />
  }
  return <DailyCalendarView {...props} />
}

const DailyCalendarView: React.FC<SummaryViewBodyProps> = ({
  config,
  numeric,
  window,
  activeDates = [],
  pagingDates = activeDates,
}) => {
  // logging_rate reuses this grid as a coverage visual: cells are simply
  // logged (green) vs missed (gray), and the banner is the % of days logged
  // (the backend's number, read straight off the payload) rather than the
  // current/longest run.
  const coverage = config?.kind === 'logging_rate'
  // Presence map (YYYY-MM-DD -> 1) from the full active-date list; the rendered
  // months below window it visually.
  const activityData = useMemo(() => {
    const out: Record<string, number> = {}
    for (const ms of activeDates) out[dayKey(ms)] = 1
    return out
  }, [activeDates])

  // The two runs we light up, windowed to the selected period so the highlights
  // and the banner agree with the Stats tab's numbers.
  const streaks = useMemo(() => computeStreaks(activeDates, window), [activeDates, window])

  // Which months to draw. A bounded window draws its own span; 'All' (±Infinity)
  // draws the span of every day with data — counted or not, so a yes/no
  // summary whose answers are all uncounted draws a grid of misses (its real
  // 0%) rather than the no-data state.
  const months = useMemo(() => {
    const { start, end } = window
    if (Number.isFinite(start) && Number.isFinite(end)) {
      // Stop at the current month: a Year (or 6M) window that includes the
      // present extends to a future Jan 1, which would otherwise render a run
      // of empty future months. Past windows (entirely before now) are
      // unaffected, so paging back still shows their full span.
      const now = new Date()
      const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime()
      return monthsInRange(start, Math.min(end, firstOfNextMonth))
    }
    const withData = [...pagingDates, ...activeDates]
    if (withData.length === 0) return []
    const min = Math.min(...withData)
    const max = Math.max(...withData)
    // +1 day so the last active day's month is included (end is exclusive).
    return monthsInRange(min, max + 86_400_000)
  }, [window, activeDates, pagingDates])

  // Active days that actually fall inside the visible window — for the testid
  // count and the empty-state decision.
  const activeInView = useMemo(() => {
    const { start, end } = window
    if (!Number.isFinite(start) || !Number.isFinite(end)) return activeDates.length
    return activeDates.filter((ms) => ms >= start && ms < end).length
  }, [window, activeDates])

  // Horizontal scrolling: the scrollbar is hidden (it crowds the small cells),
  // so on desktop a pair of chevrons below the grid does the paging. We track
  // whether the strip overflows and which ends it's parked against to drive the
  // arrows' disabled state.
  const { ref: scrollCallbackRef, scrollRef, overflow, atStart, atEnd, sync: syncScroll } =
    useHorizontalOverflow<HTMLDivElement>()

  // Start scrolled to the most recent (right-most) month so multi-month windows
  // (6M/Year) open on the present rather than on old history. Re-runs only when
  // the rendered span changes, so a manual scroll sticks within a window. The
  // hook re-measures on any later size change (card resize, late data, the
  // card's entrance settling), so the arrows aren't pinned to this one-shot
  // measurement.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollLeft = el.scrollWidth
    syncScroll()
  }, [months, scrollRef, syncScroll])

  // One chevron click pages by a single month-block (its width + the flex gap).
  const pageBy = (dir: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const step = (el.firstElementChild?.clientWidth ?? 120) + 16
    el.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  const renderCell = (date: string, count: number) => {
    // Current wins over longest so a record run (where the two coincide) reads
    // as one green color rather than needing an overlay.
    const inLongest = streaks.longestDays.has(date)
    const inCurrent = streaks.currentDays.has(date)

    // `state` names what the cell shows (its data-state test hook); a coverage
    // grid is only ever logged or empty.
    let color: string
    let note: string
    let state: 'current' | 'longest' | 'logged' | 'empty'
    if (coverage) {
      color = count > 0 ? COVERAGE_COLOR : EMPTY_COLOR
      note = ''
      state = count > 0 ? 'logged' : 'empty'
    } else if (inCurrent) {
      color = CURRENT_COLOR
      note = inLongest ? ' · current & longest streak' : ' · current streak'
      state = 'current'
    } else if (inLongest) {
      color = LONGEST_COLOR
      note = ' · longest streak'
      state = 'longest'
    } else if (count > 0) {
      color = LOGGED_COLOR
      note = ''
      state = 'logged'
    } else {
      color = EMPTY_COLOR
      note = ''
      state = 'empty'
    }

    const dObj = new Date(date + 'T00:00:00')
    const formatted = dObj.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
    return (
      <HexCell
        color={color}
        title={`${formatted}: ${count > 0 ? 'logged' : 'no check-in'}${note}`}
        day={date}
        state={state}
      />
    )
  }

  if (months.length === 0) {
    return (
      <div data-testid="summary-calendar-view" data-active-days={0}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
          <p>No check-ins yet.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="summary-calendar-view"
      data-active-days={activeInView}
      data-current={coverage ? undefined : streaks.current}
      data-longest={coverage ? undefined : streaks.longest}
    >
      {coverage ? (
        <CoverageBanner
          pct={Number(numeric?.numeric_payload?.pct ?? 0)}
          active={Number(numeric?.numeric_payload?.active_buckets ?? 0)}
          total={Number(numeric?.numeric_payload?.total_buckets ?? 0)}
          unitLabel="days"
        />
      ) : (
        <StreakBanner current={streaks.current} longest={streaks.longest} />
      )}
      <div
        ref={scrollCallbackRef}
        className="flex gap-4 overflow-x-auto py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {months.map(({ year, month }) => (
          <div key={`${year}-${month}`} className="flex-shrink-0">
            <div className="mb-1 text-center text-[10px] font-medium text-gray-500">
              {MONTH_NAMES[month]} {`'${String(year).slice(-2)}`}
            </div>
            <ActivityCalendar
              year={year}
              month={month}
              activityData={activityData}
              renderCell={renderCell}
            />
          </div>
        ))}
      </div>

      {/* Paging arrows — the hidden scrollbar's affordance. Shown at any width
          whenever the strip overflows (small screens scroll more, not less);
          touch users can still swipe directly. */}
      {overflow && (
        <div className="mt-[6px] flex items-center justify-center gap-12">
          {([['prev', -1, ChevronLeft, atStart], ['next', 1, ChevronRight, atEnd]] as const).map(
            ([key, dir, Icon, disabled]) => (
              <motion.button
                key={key}
                type="button"
                aria-label={key === 'prev' ? 'Scroll to earlier months' : 'Scroll to later months'}
                onClick={() => pageBy(dir)}
                disabled={disabled}
                whileHover={disabled ? undefined : { scale: 1.1 }}
                whileTap={disabled ? undefined : { scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="rounded-full p-0.5 text-gray-400 hover:text-gray-600 disabled:cursor-default disabled:opacity-25"
              >
                <Icon className="h-4 w-4" />
              </motion.button>
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default CalendarSummaryView
